import React, { useState, useEffect, useRef, useMemo } from 'react';
import { X, Copy, Check, DollarSign, AlertTriangle, ShieldCheck, CheckCircle2, Calendar, Hash, Clock } from 'lucide-react';
import { formatearPrecio } from '../../../../utils/utils';
import { useLogosBancos } from '../../../../config/constants';
import FileUploader from '../../../../components/FileUploader';
import { formPagoInicial } from '../../../../objetos/objeto_servicio';
import { useDetalleSolicitudModel } from '../../../../modelos/Solicitudes/modeloSolicitud';

const ModalTransferencia = ({ 
    isOpen, 
    onClose, 
    precioACobrar,
    nombreEmpresa,
    identificador
}) => {
    const { getBancoLogo } = useLogosBancos();
    const { addPago } = useDetalleSolicitudModel();

    // --- ESCUDO DE DATOS ---
    // Guardamos el precio en un Ref para que NO cambie si el padre actualiza la prop a 0
    const precioInicialRef = useRef(precioACobrar);
    const [enviadoExitoso, setEnviadoExitoso] = useState(false);
    const [cargando, setCargando] = useState(false);
    
    // Estados del formulario
    const [montoSeleccionado, setMontoSeleccionado] = useState('50');
    const [valorPersonalizado, setValorPersonalizado] = useState('');
    const [copiado, setCopiado] = useState(null);
    const [formPago, setFormPago] = useState(formPagoInicial);
    
    // Estado para el resumen final (para que no dependa de cálculos asíncronos)
    const [resumenPago, setResumenPago] = useState({ valor: 0, fecha: '' });

    const cuentas = [
        { id: 'BANCOLOMBIA', nombre: 'Bancolombia', tipo: 'Ahorros', numero: '780 000025 44' },
        { id: 'NEQUI', nombre: 'Nequi', tipo: 'Celular', numero: '3214031073' },
        { id: 'DAVIPLATA', nombre: 'Daviplata', tipo: 'Celular', numero: '3214031073' },
        { id: 'LLAVE', nombre: 'Transfiya', tipo: 'Celular', numero: '3214031073' },
    ];

    const manejarCierreVoluntario = () => {
        onClose(); // Llama al cierre del padre
        
        // Esperamos un momento a que el modal desaparezca visualmente 
        // antes de resetear el estado interno
        setTimeout(() => {
            setEnviadoExitoso(false);
            setCargando(false);
        }, 300); 
    };

    // Sincronizar el Ref solo cuando el modal se abre de cero
    useEffect(() => {
        if (isOpen && !enviadoExitoso) {
            precioInicialRef.current = precioACobrar;
            setFormPago({ archivoPago: [] });
            setValorPersonalizado('');
            setMontoSeleccionado('50');
            setCopiado(null);
            setCargando(false);
        }
    }, [isOpen]);

    // Manejo de tecla Escape
    useEffect(() => {
        const handleEsc = (e) => { if (e.key === 'Escape') manejarCierreVoluntario(); };
        if (isOpen) window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [isOpen]);

    if (!isOpen) return null;

    const obtenerValorFinal = () => {
        const base = precioInicialRef.current;
        if (montoSeleccionado === '50') return base / 2;
        if (montoSeleccionado === '100') return base;
        return parseFloat(valorPersonalizado) || 0;
    };

    const valorAPagar = obtenerValorFinal();

    const handleCopy = (num, id) => {
        navigator.clipboard.writeText(num.replace(/\s/g, ''));
        setCopiado(id);
        setTimeout(() => setCopiado(null), 2000);
    };

    const tieneArchivo = Array.isArray(formPago?.archivoPago) 
      ? formPago.archivoPago.length > 0 
      : !!formPago?.archivoPago;
    const montoValido = valorAPagar > 0 && valorAPagar <= (precioInicialRef.current + 100); // Pequeño margen por decimales
    const puedeEnviar = tieneArchivo && montoValido;

    const addPagoLocal = async (e) => {
        if (e) e.preventDefault();
        if (cargando || !puedeEnviar) return;

        setCargando(true);
        const valorFinal = obtenerValorFinal();
        const fechaActual = new Date();

        const datosFinales = {
            ...formPago,
            tipopago: 'CLIENTES',
            valor: valorFinal,
            referencia: 'MANUAL',
            metodopago: 'MANUAL',
            fechapago: fechaActual,
            estado: 'PENDIENTE',
        };

        try {
            if (!identificador || !nombreEmpresa) throw new Error("Faltan IDs");
            
            const res = await addPago(datosFinales, nombreEmpresa, identificador);
            
            if (res?.success || res) { // Dependiendo de qué devuelva tu API
                setResumenPago({
                    valor: valorFinal,
                    fecha: fechaActual.toLocaleDateString()
                });
                setEnviadoExitoso(true);
            }
        } catch (error) {
            console.error("Error al registrar el pago:", error);
            alert("Ocurrió un error al subir el comprobante.");
        } finally {
            if (!enviadoExitoso) setCargando(false);
        }
    };

    // --- VISTA DE ÉXITO ---
    if (enviadoExitoso) {
        return (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
                <div className="bg-white w-full max-w-md rounded-[32px] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
                    
                    {/* Banner de Éxito */}
                    <div className="bg-emerald-500 p-8 text-white text-center relative">
                        <div className="absolute top-4 right-4 opacity-20">
                            <ShieldCheck size={80} />
                        </div>
                        <div className="w-16 h-16 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center mx-auto mb-4 border border-white/30">
                            <Check size={32} className="text-white" strokeWidth={4} />
                        </div>
                        <h2 className="text-2xl font-black uppercase tracking-tight">¡Pago Registrado!</h2>
                        <p className="text-emerald-100 text-xs font-bold uppercase tracking-widest mt-1">ID: MANUAL-{identificador}</p>
                    </div>

                    <div className="p-8">
                        {/* Ticket de Detalles */}
                        <div className="bg-slate-50 border-2 border-dashed border-slate-200 rounded-2xl p-6 mb-6 relative">
                            {/* Círculos laterales de "Ticket" */}
                            <div className="absolute -left-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-white border-2 border-dashed border-slate-200 rounded-full"></div>
                            <div className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-white border-2 border-dashed border-slate-200 rounded-full"></div>
                            
                            <div className="space-y-4">
                                <div className="flex justify-between items-center">
                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Monto enviado</span>
                                    <span className="text-lg font-black text-slate-900">{formatearPrecio(resumenPago.valor)}</span>
                                </div>
                                <div className="flex justify-between items-center border-t border-slate-200 pt-4">
                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Fecha de reporte</span>
                                    <span className="font-bold text-slate-700">{resumenPago.fecha}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Método</span>
                                    <span className="font-bold text-slate-700">Transferencia Manual</span>
                                </div>
                            </div>
                        </div>

                        {/* SECCIÓN DE VALIDACIÓN (Más visible) */}
                        <div className="bg-blue-50 border-2 border-blue-100 rounded-2xl p-5 mb-8 flex gap-4 items-start">
                            <div className="bg-blue-600 p-2 rounded-xl text-white shrink-0 shadow-md shadow-blue-100">
                                <Clock size={20} className="animate-pulse" />
                            </div>
                            <div>
                                <h4 className="text-sm font-black text-blue-900 uppercase tracking-tight">Estado: Verificación Manual</h4>
                                <p className="text-[11px] text-blue-700 leading-relaxed mt-1">
                                    Nuestro equipo de contabilidad revisará tu comprobante. Este proceso suele tardar entre <strong>15 y 60 minutos</strong> en horario laboral.
                                </p>
                            </div>
                        </div>

                        {/* Mensaje de Notificación */}
                        <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-xl mb-8 border border-slate-100">
                            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-ping"></div>
                            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">
                                Te notificaremos vía email apenas sea aprobado.
                            </p>
                        </div>

                        <button 
                            onClick={manejarCierreVoluntario} 
                            className="w-full py-4 bg-slate-900 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-slate-800 transition-all shadow-xl active:scale-95"
                        >
                            Entendido
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // --- VISTA FORMULARIO ---
    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300">
            <div className="bg-white w-full max-w-2xl rounded-[32px] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
                <div className="px-8 pt-8 pb-4 flex items-center justify-between relative bg-white">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600 shadow-sm border border-blue-100">
                            <ShieldCheck size={28} />
                        </div>
                        <div>
                            <h2 className="text-xl font-extrabold text-slate-900 leading-none flex items-center gap-2">Pago Seguro <span className="text-blue-600">🦉</span></h2>
                            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-[0.15em] mt-1">Transferencia Directa</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2.5 bg-slate-50 hover:bg-red-50 text-slate-400 hover:text-red-500 rounded-xl transition-all duration-200 border border-slate-100"><X size={20} /></button>
                </div>

                <div className="p-8 max-h-[80vh] overflow-y-auto custom-scrollbar">
                    <div className="mb-8 text-center">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-4">1. ¿Cuánto deseas abonar hoy?</label>
                        <div className="grid grid-cols-3 gap-3 mb-4">
                            <button onClick={() => setMontoSeleccionado('50')} className={`p-4 rounded-2xl border-2 transition-all flex flex-col items-center justify-center ${montoSeleccionado === '50' ? 'border-blue-600 bg-blue-50' : 'border-gray-100 hover:bg-gray-50'}`}>
                                <p className="text-[9px] font-bold text-gray-400 uppercase">Abono 50%</p>
                                <p className="text-sm font-black text-blue-900">{formatearPrecio(precioInicialRef.current / 2)}</p>
                            </button>
                            <button onClick={() => setMontoSeleccionado('100')} className={`p-4 rounded-2xl border-2 transition-all flex flex-col items-center justify-center ${montoSeleccionado === '100' ? 'border-emerald-500 bg-emerald-50' : 'border-gray-100 hover:bg-gray-50'}`}>
                                <p className="text-[9px] font-bold text-gray-400 uppercase">Total 100%</p>
                                <p className="text-sm font-black text-emerald-700">{formatearPrecio(precioInicialRef.current)}</p>
                            </button>
                            <button onClick={() => setMontoSeleccionado('custom')} className={`p-4 rounded-2xl border-2 transition-all flex flex-col items-center justify-center ${montoSeleccionado === 'custom' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-100 hover:bg-gray-50'}`}>
                                <p className="text-[9px] font-bold text-gray-400 uppercase">Otro valor</p>
                                <p className="text-sm font-black text-indigo-700">Personalizar</p>
                            </button>
                        </div>
                        {montoSeleccionado === 'custom' && (
                            <div className="animate-in slide-in-from-top-2 duration-300 relative">
                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                    <DollarSign size={18} className={valorAPagar > precioInicialRef.current ? "text-red-500" : "text-indigo-500"} />
                                </div>
                                <input type="number" placeholder="Monto a pagar" value={valorPersonalizado} onChange={(e) => setValorPersonalizado(e.target.value)} className={`w-full pl-10 pr-4 py-4 rounded-2xl font-bold border-2 focus:outline-none ${valorAPagar > precioInicialRef.current ? 'bg-red-50 border-red-300 text-red-900' : 'bg-indigo-50 border-indigo-200 text-indigo-900'}`}/>
                            </div>
                        )}
                    </div>

                    <div className="mb-8">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-3">2. Datos de destino</label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {cuentas.map((c) => (
                                <div key={c.id} className="bg-slate-50 p-3 rounded-2xl border border-slate-100 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <img src={getBancoLogo(c.id)} className="w-8 h-8 rounded-lg shadow-sm" alt={c.nombre} />
                                        <div><p className="text-[9px] font-bold text-gray-400 uppercase leading-none">{c.nombre}</p><p className="font-mono text-xs font-bold text-slate-700">{c.numero}</p></div>
                                    </div>
                                    <button onClick={() => handleCopy(c.numero, c.id)} className={`p-1.5 rounded-lg ${copiado === c.id ? 'bg-green-500 text-white' : 'bg-white text-slate-400 border hover:text-blue-600'}`}>{copiado === c.id ? <Check size={14} /> : <Copy size={14} />}</button>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="mb-8">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-3">3. Sube tu comprobante</label>
                        <div className="border-2 border-dashed border-slate-200 rounded-2xl p-4 bg-slate-50">
                            <FileUploader 
                            formData={formPago} 
                            setFormData={setFormPago} 
                            fieldName="archivoPago" 
                            //allowedTypes={['application/pdf','image/*']} 
                            maxSize={100 * 1024 * 1024} l
                            abel="Adjuntar foto del pago" 
                            multiple={false} 
                            />
                        </div>
                    </div>

                    <div className="bg-slate-900 rounded-2xl p-4 mb-6 flex justify-between items-center text-white">
                        <span className="text-xs font-bold uppercase opacity-60">Total a validar:</span>
                        <span className="text-xl font-black">{formatearPrecio(valorAPagar)}</span>
                    </div>

                    <button type="button" disabled={!puedeEnviar || cargando} onClick={addPagoLocal} className={`w-full py-4 rounded-2xl font-black uppercase flex items-center justify-center gap-2 transition-all ${puedeEnviar && !cargando ? 'bg-blue-600 text-white shadow-xl hover:bg-blue-700' : 'bg-slate-100 text-slate-400'}`}>
                        {cargando ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><ShieldCheck size={20} /> Confirmar pago</>}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- EL SEGURO DE VIDA: React.memo ---
export default React.memo(ModalTransferencia, (prev, next) => {
    // Solo re-renderiza si el modal se abre o se cierra.
    // Ignora cambios de precioACobrar o identificador mientras esté abierto.
    return prev.isOpen === next.isOpen;
});